import {
  signUp,
  confirmSignUp,
  signIn,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
  autoSignIn,
  type SignUpOutput,
  type ConfirmSignUpOutput,
  type SignInOutput,
  type ResetPasswordOutput,
} from 'aws-amplify/auth';

// Auth error types for consistent error handling
export type AuthErrorCode =
  | 'UserNotFoundException'
  | 'NotAuthorizedException'
  | 'UserNotConfirmedException'
  | 'UsernameExistsException'
  | 'InvalidPasswordException'
  | 'CodeMismatchException'
  | 'ExpiredCodeException'
  | 'LimitExceededException'
  | 'InvalidParameterException'
  | 'NetworkError'
  | 'UnknownError';

export interface AuthError {
  code: AuthErrorCode;
  message: string;
}

export interface AuthResult<T> {
  success: boolean;
  data?: T;
  error?: AuthError;
}

/**
 * Map Amplify auth errors to user-friendly messages
 */
function mapAuthError(error: unknown): AuthError {
  const err = error as { name?: string; message?: string };
  const name = err.name || '';
  const message = err.message || '';

  // Check for network errors
  if (message.includes('Network') || message.includes('fetch')) {
    return {
      code: 'NetworkError',
      message: 'Connection error. Please check your internet connection.',
    };
  }

  // Map Cognito error names to user-friendly messages
  switch (name) {
    case 'UserNotFoundException':
      return {
        code: 'UserNotFoundException',
        message: 'No account found with this email address.',
      };
    case 'NotAuthorizedException':
      return {
        code: 'NotAuthorizedException',
        message: 'Invalid email or password.',
      };
    case 'UserNotConfirmedException':
      return {
        code: 'UserNotConfirmedException',
        message: 'Please verify your email address first.',
      };
    case 'UsernameExistsException':
      return {
        code: 'UsernameExistsException',
        message: 'An account with this email already exists.',
      };
    case 'InvalidPasswordException':
      return {
        code: 'InvalidPasswordException',
        message: 'Password does not meet the requirements.',
      };
    case 'CodeMismatchException':
      return {
        code: 'CodeMismatchException',
        message: 'Invalid verification code. Please try again.',
      };
    case 'ExpiredCodeException':
      return {
        code: 'ExpiredCodeException',
        message: 'Verification code has expired. Please request a new one.',
      };
    case 'LimitExceededException':
      return {
        code: 'LimitExceededException',
        message: 'Too many attempts. Please try again later.',
      };
    case 'InvalidParameterException':
      return {
        code: 'InvalidParameterException',
        message: 'Invalid input. Please check your entries.',
      };
    default:
      // Unhandled auth error (suppressed)
      return {
        code: 'UnknownError',
        message: 'An unexpected error occurred. Please try again.',
      };
  }
}

/**
 * Register a new user with email and password
 */
export async function registerUser(
  email: string,
  password: string
): Promise<AuthResult<SignUpOutput>> {
  try {
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
        autoSignIn: true,
      },
    });

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    return {
      success: false,
      error: mapAuthError(error),
    };
  }
}

/**
 * Confirm user registration with verification code
 */
export async function confirmUserSignup(
  email: string,
  code: string
): Promise<AuthResult<ConfirmSignUpOutput>> {
  try {
    const result = await confirmSignUp({
      username: email,
      confirmationCode: code,
    });

    // If auto sign-in is enabled, it will trigger automatically
    // via the Hub listener in useAuth
    if (result.isSignUpComplete) {
      try {
        await autoSignIn();
      } catch {
        // Auto sign-in might fail if not configured, that's okay
      }
    }

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    return {
      success: false,
      error: mapAuthError(error),
    };
  }
}

/**
 * Resend verification code
 */
export async function resendVerificationCode(
  email: string
): Promise<AuthResult<void>> {
  try {
    await resendSignUpCode({
      username: email,
    });

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: mapAuthError(error),
    };
  }
}

/**
 * Sign in with email and password
 */
export async function loginUser(
  email: string,
  password: string
): Promise<AuthResult<SignInOutput>> {
  try {
    const result = await signIn({
      username: email,
      password,
    });

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    return {
      success: false,
      error: mapAuthError(error),
    };
  }
}

/**
 * Initiate password reset flow
 */
export async function requestPasswordReset(
  email: string
): Promise<AuthResult<ResetPasswordOutput>> {
  try {
    const result = await resetPassword({
      username: email,
    });

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    return {
      success: false,
      error: mapAuthError(error),
    };
  }
}

/**
 * Complete password reset with verification code and new password
 */
export async function completePasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<AuthResult<void>> {
  try {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: mapAuthError(error),
    };
  }
}
